<template>
  <DialogLayout class-name="image-rotate-dialog" @close="emit('close')">
    <template #title>{{ dialogTitle }}</template>

    <div class="image-rotate-content">
      <!-- 展示区域尺寸固定，图片按比例缩放，旋转后不会超出区域也不会变形 -->
      <div class="image-rotate-stage-wrap">
        <button
          v-if="hasMultiple"
          type="button"
          class="image-rotate-nav"
          :disabled="isFirst || confirming"
          @click="goPrev"
        >
          <Icon name="arrow-left" size="lg" />
        </button>

        <div class="image-rotate-stage" :style="stageStyle">
          <img
            v-if="currentObjectUrl"
            :key="currentIndex"
            ref="imageRef"
            :src="currentObjectUrl"
            :alt="currentFile?.name"
            class="image-rotate-preview"
            :style="imageStyle"
            @load="handleImageLoad"
            @error="handleImageError"
          />
        </div>

        <button
          v-if="hasMultiple"
          type="button"
          class="image-rotate-nav image-rotate-nav--next"
          :disabled="isLast || confirming"
          @click="goNext"
        >
          <Icon name="arrow-left" size="lg" />
        </button>
      </div>

      <div class="image-rotate-actions">
        <el-button class="image-rotate-button" type="primary" plain :disabled="!loaded" @click="rotate(-90)">
          {{ t('common.imageUpload.rotateLeft') }}
        </el-button>
        <el-button class="image-rotate-button" type="primary" plain :disabled="!loaded" @click="rotate(90)">
          {{ t('common.imageUpload.rotateRight') }}
        </el-button>
      </div>
    </div>

    <template #footer>
      <el-button class="confirm-button-default" @click="emit('cancel')">{{ t('common.cancel') }}</el-button>
      <el-button type="primary" :loading="confirming" :disabled="!loaded" @click="handleConfirm">
        {{ t('common.confirm1') }}
      </el-button>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import { Icon } from '@/components/basic-components/icon'
import type { ImageRotateDialogProps } from './image-rotate-dialog-types'
import { rotateImageFile } from '@/utils/image'

const props = withDefaults(defineProps<ImageRotateDialogProps>(), {
  title: '',
  maxWidth: 480,
  maxHeight: 360
})

const emit = defineEmits<{
  /** 确认后抛出旋转处理后的文件列表，顺序与传入的 files 一致 */
  confirm: [files: File[]]
  cancel: []
  close: []
}>()

const { t } = useI18n()

const dialogTitle = computed(() => props.title || t('common.imageUpload.rotateTitle'))

const currentIndex = ref(0)
const hasMultiple = computed(() => props.files.length > 1)
const isFirst = computed(() => currentIndex.value === 0)
const isLast = computed(() => currentIndex.value === props.files.length - 1)
const currentFile = computed(() => props.files[currentIndex.value])

// 一次性为所有文件创建预览地址，切换图片时无需重新读取文件
const objectUrls = props.files.map((file) => URL.createObjectURL(file))
onBeforeUnmount(() => {
  objectUrls.forEach((url) => URL.revokeObjectURL(url))
})
const currentObjectUrl = computed(() => objectUrls[currentIndex.value] || '')

const imageRef = ref<HTMLImageElement | null>(null)
const naturalWidth = ref(0)
const naturalHeight = ref(0)
const loaded = ref(false)
const confirming = ref(false)
/**
 * 每张图片累加的旋转角度（顺时针为正，可为负数或超过 360），按 files 下标对应。
 * 不归一化到 0~359，否则 CSS 过渡会按最短路径之外的方向绕行，
 * 例如第一次左旋转从 0deg 变成 270deg，看起来是往右转了一圈
 */
const rotations = ref<number[]>(props.files.map(() => 0))
const rotation = computed({
  get: () => rotations.value[currentIndex.value] ?? 0,
  set: (value: number) => {
    rotations.value[currentIndex.value] = value
  }
})

// 切换图片时旋转按钮需要等新图片加载完成后才可点击
watch(currentIndex, () => {
  loaded.value = false
})

const goPrev = () => {
  if (isFirst.value || confirming.value) return
  currentIndex.value -= 1
}

const goNext = () => {
  if (isLast.value || confirming.value) return
  currentIndex.value += 1
}

const handleImageLoad = () => {
  const el = imageRef.value
  if (!el) return
  naturalWidth.value = el.naturalWidth
  naturalHeight.value = el.naturalHeight
  loaded.value = true
}

const handleImageError = () => {
  loaded.value = false
  ElMessage.error(t('common.imgUpload.loadFailed'))
}

const rotate = (deg: number) => {
  rotation.value += deg
}

/** 归一化到 0 / 90 / 180 / 270，用于布局与出图 */
const normalizedRotation = computed(() => ((rotation.value % 360) + 360) % 360)

/** 90 / 270 度时图片的宽高在视觉上互换 */
const isSwapped = computed(() => normalizedRotation.value % 180 !== 0)

/**
 * 按原始比例计算展示尺寸：旋转后宽高互换，
 * 所以互换时用「高度」限制元素宽度、用「宽度」限制元素高度
 */
const displaySize = computed(() => {
  const width = naturalWidth.value
  const height = naturalHeight.value
  if (!width || !height) return { width: 0, height: 0 }
  const limitWidth = isSwapped.value ? props.maxHeight : props.maxWidth
  const limitHeight = isSwapped.value ? props.maxWidth : props.maxHeight
  const scale = Math.min(1, limitWidth / width, limitHeight / height)
  return { width: width * scale, height: height * scale }
})

const stageStyle = computed(() => ({
  width: `${props.maxWidth}px`,
  height: `${props.maxHeight}px`
}))

const imageStyle = computed(() => ({
  width: displaySize.value.width ? `${displaySize.value.width}px` : 'auto',
  height: displaySize.value.height ? `${displaySize.value.height}px` : 'auto',
  transform: `rotate(${rotation.value}deg)`
}))

const handleConfirm = async () => {
  if (!props.files.length) {
    emit('cancel')
    return
  }
  confirming.value = true
  try {
    // 上传的是旋转之后的图片，后续预览到的也是旋转后的结果
    const files = await Promise.all(
      props.files.map((file, index) => rotateImageFile(file, rotations.value[index] ?? 0))
    )
    emit('confirm', files)
  } catch (error) {
    console.error('图片旋转失败:', error)
    ElMessage.error(t('common.imageUpload.rotateFailed'))
  } finally {
    confirming.value = false
  }
}
</script>

<style lang="scss">
.image-rotate-dialog {
  min-width: 540px;
}
</style>

<style scoped lang="scss">
.image-rotate-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.image-rotate-stage-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 100%;
}

.image-rotate-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
  background: #f7f8fa;
  border-radius: 8px;
}

.image-rotate-preview {
  display: block;
  object-fit: contain;
  // 宽高在旋转 90/270 度时会互换，一起做过渡避免尺寸突变
  transition: transform 0.3s ease, width 0.3s ease, height 0.3s ease;
}

.image-rotate-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  background: transparent;
  border: none;
  color: #495057;
  cursor: pointer;
  transition: color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--el-color-primary);
  }

  &:disabled {
    color: #c9d3d9;
    cursor: not-allowed;
  }

  &--next {
    transform: rotate(180deg);
  }
}

.image-rotate-actions {
  display: flex;
  gap: 16px;
}

.image-rotate-button {
  min-width: 108px;
  height: 35px;
}
</style>
